import React, { useState, useRef, useCallback, startTransition } from "react";
import Galaxy from "./components/Galaxy";
import SpaceCursor, { HoverContext } from "./components/SpaceCursor";
import SpaceHUD from "./components/SpaceHUD";
import IntroSequence from "./components/IntroSequence";
import TutorialOverlay from "./components/TutorialOverlay";
import SidePanel from "./components/SidePanel";

// Section content components
import Profile              from "./components/sections/ProfileSection";
import Experience           from "./components/Experience";
import Projects             from "./components/Projects";
import Skills               from "./components/Skills";
import Education            from "./components/Education";
import Certifications       from "./components/Certifications";
import Contact              from "./components/Contact";
import Interests            from "./components/Interests";
import Testimonials         from "./components/Testimonials";
import AssociativeExperience from "./components/AssociativeExperience";

import { Analytics } from "@vercel/analytics/react";
import "./App.css";
import "./styles/themes.css";

const getSectionComponent = (section, navigateToSection) => {
  switch (section) {
    case "profile":        return <Profile onNavigate={navigateToSection} />;
    case "experience":     return <Experience />;
    case "projects":       return <Projects />;
    case "skills":         return <Skills />;
    case "education":      return <Education />;
    case "certifications": return <Certifications />;
    case "contact":        return <Contact />;
    case "interests":      return <Interests />;
    case "testimonials":   return <Testimonials />;
    case "associative":    return <AssociativeExperience />;
    default:               return null;
  }
};

const App = () => {
  const [introPhase, setIntroPhase]         = useState("intro");
  const [currentSection, setCurrentSection] = useState(null);
  const [isPlaying, setIsPlaying]           = useState(false);
  const [hovering, setHovering]             = useState(null);
  const audioRef          = useRef(null);
  const controlsRef       = useRef();
  const galaxyFlyRef      = useRef(null); // receives flyToPlanet from Galaxy
  const galaxyViewFlyRef  = useRef(null); // receives flyForView (tutorial, no panel)

  // Navigate to a section without flying (just swaps panel content).
  // Bails out if the section is already current so the panel doesn't re-mount.
  const navigateToSection = useCallback((section) => {
    startTransition(() => {
      setCurrentSection(prev =>
        prev?.section === section ? prev : (section ? { section } : null)
      );
    });
  }, []);

  // Called by SectionMenu — triggers cinematic fly-to then opens panel
  const flyAndNavigate = useCallback((section, planetName) => {
    if (galaxyFlyRef.current && planetName) {
      galaxyFlyRef.current(planetName);
    } else {
      navigateToSection(section);
    }
  }, [navigateToSection]);

  const handlePlanetClick = useCallback(({ section }) => {
    navigateToSection(section);
  }, [navigateToSection]);

  const handlePanelClose = useCallback(() => {
    setCurrentSection(null);
  }, []);

  // Called by SidePanel prev/next — immediately updates panel content AND
  // flies the camera to the corresponding planet.
  const handlePanelNavigate = useCallback((section, planetName) => {
    // Update panel content right away for instant feedback
    startTransition(() => {
      setCurrentSection(prev =>
        prev?.section === section ? prev : { section }
      );
    });
    // Fly camera to the next planet
    if (galaxyFlyRef.current && planetName) {
      galaxyFlyRef.current(planetName);
    }
  }, []);

  const handleAudioToggle = useCallback(() => {
    if (isPlaying) {
      audioRef.current?.pause();
      if (audioRef.current) audioRef.current.currentTime = 0;
      setIsPlaying(false);
    } else {
      if (!audioRef.current) {
        audioRef.current = new Audio("/Interstellar.mp3");
        audioRef.current.loop = true;
      }
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, [isPlaying]);

  const handleRecenter = useCallback(() => {
    controlsRef.current?.reset();
  }, []);

  return (
    <HoverContext.Provider value={{ hovering, setHovering }}>
      <div className="app">

        {/* Vercel Analytics */}
        <Analytics />

        {/* Custom cursor */}
        <SpaceCursor />

        {/* 3D Galaxy — always mounted, always visible.
            focusedSection tells Galaxy which planet to freeze and
            disables autoRotate while the panel is open. */}
        <Galaxy
          onPlanetClick={handlePlanetClick}
          bloomEnabled={true}
          controlsRef={controlsRef}
          onFlyReady={(fn) => { galaxyFlyRef.current = fn; }}
          onViewFlyReady={(fn) => { galaxyViewFlyRef.current = fn; }}
          focusedSection={currentSection?.section}
        />

        {/* Cinematic intro */}
        {introPhase === "intro" && (
          <IntroSequence onComplete={() => setIntroPhase("tutorial")} />
        )}

        {/* Guided tutorial — plays after intro, before free exploration */}
        {introPhase === "tutorial" && (
          <TutorialOverlay
            flyForView={(name) => galaxyViewFlyRef.current?.(name)}
            resetCamera={() => controlsRef.current?.reset()}
            onComplete={() => {
              localStorage.setItem("ela-portfolio-visited", "1");
              setIntroPhase("active");
            }}
          />
        )}

        {/* HUD — visible after intro, always (even when panel open) */}
        {introPhase === "active" && (
          <SpaceHUD
            isPlaying={isPlaying}
            onAudioToggle={handleAudioToggle}
            onRecenter={handleRecenter}
            onPlanetSelect={flyAndNavigate}
            controlsRef={controlsRef}
            activeSection={currentSection?.section}
          />
        )}

        {/* Side panel — slides in from right, solar system stays visible */}
        {currentSection && (
          <SidePanel
            section={currentSection.section}
            onClose={handlePanelClose}
            onNavigate={handlePanelNavigate}
          >
            {getSectionComponent(currentSection.section, navigateToSection)}
          </SidePanel>
        )}

      </div>
    </HoverContext.Provider>
  );
};

export default App;
